import React, { useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import classnames from 'classnames'
import find from 'lodash/find'
import ProjectsTableHeader from '@SiteContainers/ProjectsTableHeader'
import ProjectsBulkActionBar from '@SiteContainers/ProjectsBulkActionBar'
import ProjectsTableList from './ProjectsTableList'
import ProjectsTableGrid from './ProjectsTableGrid'

import {
  IProject,
} from '@SiteModules/Project/types'

import {
  IProjectVersion,
} from '@SiteModules/ProjectVersion/types'

import {
  IOpenRenameProjectPopupPayload,
  IOpenDeleteProjectPopupPayload,
  IOpenSetupProjectPopupPayload,
  IOpenSetupProjectPopupTtsPayload,
} from '@SiteModules/Popup/types'

import {
  getCurrentWorkspaceId,
} from '@SiteModules/Workspace/selectors'

import {
  getProjects,
  getSelectedProjectIds,
  getShouldUpdateProjectVersionsAndSubtitles,
  getSubtitleIdsToUpdate,
  getProjectVersionIdsToUpdate,
  getCurrentPage,
  getTotalPages,
  getTotalCount,
  getShouldCheckAutoFillList,
  getShouldCheckAutoFillGrid,
  getLoading,
  getProjectLabelCaptions,
  getHasLoadedInitially as getHasLoadedProjectsInitially,
  getSearchText,
} from '@SiteModules/Project/selectors'

import {
  getSelectedLabelIds,
} from '@SiteModules/Label/selectors'

import {
  getHasLoadedUserDataSuccessfully,
  getNumberOfProjectsSuccessfullyCreated,
  getShouldSeeNewsletterPopup,
} from '@SiteModules/User/selectors'

import {
  getSetupProjectPopupIsOpen,
} from '@SiteModules/Popup/selectors'

import {
  getCurrentAccountHasCurrentSubscription,
} from '@SiteModules/Account/selectors'

import {
  openNewProjectPopup,
  openSetupProjectPopup,
  openSetupProjectPopupTts,
  openRenameProjectPopup,
  openDeleteProjectPopup,
  openNewsletterPopup,
  openRenewSubscriptionPopup,
  openExportationPopup,
} from '@SiteModules/Popup/actions'

import {
  resetShouldCheckAutoFillGrid,
  resetShouldCheckAutoFillList,
  setShouldCheckAutoFill,
  changeSelectedProjects,
  updateProjects,
  fetchProjects,
} from '@SiteModules/Project/actions'

import './ProjectsTable.scss'

const UPDATE_PROJECTS_DELAY = 60000
const MIN_SUCCESSFUL_PROJECTS_BEFORE_NEWSLETTER_POPUP = 2

interface ProjectsTableProps {
  readonly showDashboardWithFullLanguagesOrProjectVersionsView: boolean
  readonly listActive: boolean
  setOpenedProjectId(projectId: string): void
  setOpenedProjectVersionId(projectVersionId: string): void
  setListActive(newSetListActive: boolean): void
}

const ProjectsTable: React.FC<ProjectsTableProps> = props => {
  const dispatch = useDispatch()

  const currentWorkspaceId = useSelector(getCurrentWorkspaceId)
  const projects = useSelector(getProjects)
  const selectedProjectIds = useSelector(getSelectedProjectIds)
  const subtitlesToUpdateIds = useSelector(getSubtitleIdsToUpdate)
  const projectVersionsToUpdateIds = useSelector(getProjectVersionIdsToUpdate)
  const shouldUpdateProjectVersionsAndSubtitles = useSelector(getShouldUpdateProjectVersionsAndSubtitles)
  const projectLabelCaptions = useSelector(getProjectLabelCaptions)
  const selectedLabelIds = useSelector(getSelectedLabelIds)
  const currentPage = useSelector(getCurrentPage)
  const totalPages = useSelector(getTotalPages)
  const totalCountOfProjects = useSelector(getTotalCount)
  const shouldCheckAutoFillList = useSelector(getShouldCheckAutoFillList)
  const shouldCheckAutoFillGrid = useSelector(getShouldCheckAutoFillGrid)
  const loading = useSelector(getLoading)
  const hasLoadedUserDataSuccessfully = useSelector(getHasLoadedUserDataSuccessfully)
  const hasLoadedProjectsInitially = useSelector(getHasLoadedProjectsInitially)
  const setupProjectPopupIsOpen = useSelector(getSetupProjectPopupIsOpen)
  const numberOfProjectsSuccessfullyCreated = useSelector(getNumberOfProjectsSuccessfullyCreated)
  const shouldSeeNewsletterPopup = useSelector(getShouldSeeNewsletterPopup)
  const searchText = useSelector(getSearchText)
  const currentAccountHasCurrentSubscription = useSelector(getCurrentAccountHasCurrentSubscription)

  const hasProjectsSelected = selectedProjectIds.length > 0
  const hasLoadedAllProjects = hasLoadedProjectsInitially && projects.length === totalCountOfProjects
  const hasValidCurrentWorkspace = currentWorkspaceId >= 0

  const subtitlesToUpdateIdsRef = useRef(subtitlesToUpdateIds)
  const projectVersionsToUpdateIdsRef = useRef(projectVersionsToUpdateIds)
  const selectedLabelIdsRef = useRef(selectedLabelIds)
  const workspaceIdRef = useRef(currentWorkspaceId)

  const {
    showDashboardWithFullLanguagesOrProjectVersionsView,
    listActive,
  } = props

  useEffect(() => {
    const hasEnoughProjectsCreated = numberOfProjectsSuccessfullyCreated >= MIN_SUCCESSFUL_PROJECTS_BEFORE_NEWSLETTER_POPUP

    const shouldOpenNewsletterPopup =
      hasLoadedProjectsInitially &&
      hasLoadedUserDataSuccessfully &&
      !setupProjectPopupIsOpen &&
      hasEnoughProjectsCreated &&
      shouldSeeNewsletterPopup

    if (shouldOpenNewsletterPopup) {
      dispatch(openNewsletterPopup())
    }
  }, [
    dispatch,
    hasLoadedProjectsInitially,
    hasLoadedUserDataSuccessfully,
    numberOfProjectsSuccessfullyCreated,
    setupProjectPopupIsOpen,
    shouldSeeNewsletterPopup,
  ])

  useEffect(() => {
    const scrollContainer = listActive ? document.querySelector('.ProjectsTableList__body') : document.querySelector('.ProjectsTableGrid')

    if (!scrollContainer) { return }

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer
      const nearBottom = scrollHeight - scrollTop - clientHeight < window.Checksub.nearBottomDistance
      const hasProjectsToLoad = projects.length < totalCountOfProjects
      const hasPagesToLoad = currentPage < totalPages

      if (!loading && nearBottom && hasProjectsToLoad && hasPagesToLoad) {
        dispatch(fetchProjects({
          selectedLabels: selectedLabelIds,
          workspaceId: currentWorkspaceId,
          page: currentPage + 1,
          ...(searchText.length > 0 ? { searchText } : {}),
        }))
      }
    }

    const handleSetShouldCheckAutoFill = () => {
      dispatch(setShouldCheckAutoFill())
    }

    scrollContainer.addEventListener('scroll', handleScroll)
    window.addEventListener('resize', handleSetShouldCheckAutoFill)

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleSetShouldCheckAutoFill)
    }
  }, [listActive, currentPage, totalPages, projects, totalCountOfProjects, currentWorkspaceId, selectedLabelIds, loading])

  useEffect(() => {
    // Handle the case when the first page of project versions does not lead to a scrollable container because it does not fill the viewport. In this case repeated fetching is needed until the container becomes scrollable
    const checkScrollability = (resetAutoFillList: boolean) => {
      const scrollContainer = listActive
        ? document.querySelector('.ProjectsTableList__body')
        : document.querySelector('.ProjectsTableGrid')

      if (!scrollContainer || scrollContainer.scrollHeight === 0 || scrollContainer.clientHeight === 0) { return }

      const isScrollable = scrollContainer.scrollHeight > scrollContainer.clientHeight
      const hasProjectsToLoad = projects.length < totalCountOfProjects
      const hasPagesToLoad = currentPage < totalPages

      if (!loading && !isScrollable && hasProjectsToLoad && hasPagesToLoad) {
        dispatch(fetchProjects({
          selectedLabels: selectedLabelIds,
          workspaceId: currentWorkspaceId,
          page: currentPage + 1,
          ...(searchText.length > 0 ? { searchText } : {}),
        }))
      } else {
        resetAutoFillList ? dispatch(resetShouldCheckAutoFillList()) : dispatch(resetShouldCheckAutoFillGrid())
      }
    }

    if (listActive && shouldCheckAutoFillList) {
      checkScrollability(true)
    }

    if (!listActive && shouldCheckAutoFillGrid) {
      checkScrollability(false)
    }
  }, [shouldCheckAutoFillGrid, shouldCheckAutoFillList, listActive, projects, totalCountOfProjects, currentWorkspaceId, currentPage, totalPages, selectedLabelIds, loading])

  useEffect(() => {
    subtitlesToUpdateIdsRef.current = subtitlesToUpdateIds
    projectVersionsToUpdateIdsRef.current = projectVersionsToUpdateIds
    selectedLabelIdsRef.current = selectedLabelIds
    workspaceIdRef.current = currentWorkspaceId
  }, [subtitlesToUpdateIds, projectVersionsToUpdateIds, selectedLabelIds, currentWorkspaceId])

  useEffect(() => {
    if (!shouldUpdateProjectVersionsAndSubtitles) { return }

    const id = window.setInterval(() => {
      dispatch(updateProjects({
        subtitlesToUpdateIds: subtitlesToUpdateIdsRef.current,
        projectVersionsToUpdateIds: projectVersionsToUpdateIdsRef.current,
        selectedLabels: selectedLabelIdsRef.current,
        workspaceId: workspaceIdRef.current,
      }))
    }, UPDATE_PROJECTS_DELAY)

    return () => {
      clearInterval(id)
    }
  }, [shouldUpdateProjectVersionsAndSubtitles])

  const handleOpenNewProjectPopup = () => {
    dispatch(openNewProjectPopup())
  }

  const handleOpenRenewSubscriptionPopup = () => {
    dispatch(openRenewSubscriptionPopup())
  }

  const handleOpenRenameProjectPopup = (payload: IOpenRenameProjectPopupPayload) => {
    dispatch(openRenameProjectPopup(payload))
  }

  const handleOpenDeleteProjectPopup = (payload: IOpenDeleteProjectPopupPayload) => {
    dispatch(openDeleteProjectPopup(payload))
  }

  const handleOpenSetupProjectPopup = (payload: IOpenSetupProjectPopupPayload) => {
    dispatch(openSetupProjectPopup(payload))
  }

  const handleOpenSetupProjectPopupTts = (payload: IOpenSetupProjectPopupTtsPayload) => {
    dispatch(openSetupProjectPopupTts(payload))
  }

  const handleChangeSelectedProjects = (e: React.ChangeEvent<HTMLInputElement>, projectId: string) => {
    dispatch(changeSelectedProjects({ projectId, selectProject: e.target.checked }))
  }

  const handleForwarding = (project: IProject) => {
    const {
      projectVersions
    } = project
    const originalProjectVersion = find(projectVersions, pv => !pv.attributes.isRepurposed) as IProjectVersion

    const { subtitles, id } = originalProjectVersion
    const { projectId, status, isPreparingRepurposedClips } = originalProjectVersion.attributes

    const shouldShowLinkToEditor = subtitles.length === 0 && status !== 'downloading' && !isPreparingRepurposedClips
    const shouldOpenLanguagesView = projectVersions.length === 1 && (subtitles.length > 0 || isPreparingRepurposedClips)
    const shouldOpenProjectVersionsView = projectVersions.length > 1 && subtitles.length > 0
    const shouldShowLinkForName = shouldShowLinkToEditor || shouldOpenLanguagesView || shouldOpenProjectVersionsView

    if (!shouldShowLinkForName) { return }

    if (shouldShowLinkToEditor) {
      window.location.href = `/projects/${projectId}/project_versions/${id}/?projectSettings=true`
    } else if (shouldOpenLanguagesView) {
      props.setOpenedProjectVersionId(originalProjectVersion.id)
    } else if (shouldOpenProjectVersionsView) {
      props.setOpenedProjectId(project.id)
    }
  }

  const handleOpenExportationPopup = (projectId: string) => {
    dispatch(openExportationPopup({ projectIds: [projectId] }))
  }

  const renderProjectsTableMain = () => {
    return (
      <div className="ProjectsTable__main">
        <ProjectsTableHeader
          openNewProjectPopup={handleOpenNewProjectPopup}
          openRenewSubscriptionPopup={handleOpenRenewSubscriptionPopup}
          listActive={listActive}
          setListActive={props.setListActive}
        />
        <ProjectsTableList
          className={classnames('ProjectsTableList', { hidden: !listActive })}
          projects={projects}
          currentAccountHasCurrentSubscription={currentAccountHasCurrentSubscription}
          changeSelectedProjects={handleChangeSelectedProjects}
          selectedProjectIds={selectedProjectIds}
          handleForwarding={handleForwarding}
          handleOpenRenameProjectPopup={handleOpenRenameProjectPopup}
          handleOpenDeleteProjectPopup={handleOpenDeleteProjectPopup}
          handleOpenSetupProjectPopup={handleOpenSetupProjectPopup}
          handleOpenSetupProjectPopupTts={handleOpenSetupProjectPopupTts}
          handleOpenRenewSubscriptionPopup={handleOpenRenewSubscriptionPopup}
          loading={loading}
          hasLoadedAllProjects={hasLoadedAllProjects}
          handleOpenExportationPopup={handleOpenExportationPopup}
        />
        <ProjectsTableGrid
          className={classnames('ProjectsTableGrid', { hidden: listActive })}
          projects={projects}
          changeSelectedProjects={handleChangeSelectedProjects}
          selectedProjectIds={selectedProjectIds}
          openNewProjectPopup={handleOpenNewProjectPopup}
          openRenewSubscriptionPopup={handleOpenRenewSubscriptionPopup}
          handleForwarding={handleForwarding}
          handleOpenRenameProjectPopup={handleOpenRenameProjectPopup}
          handleOpenDeleteProjectPopup={handleOpenDeleteProjectPopup}
          handleOpenSetupProjectPopup={handleOpenSetupProjectPopup}
          handleOpenSetupProjectPopupTts={handleOpenSetupProjectPopupTts}
          handleOpenRenewSubscriptionPopup={handleOpenRenewSubscriptionPopup}
          loading={loading}
          projectLabelCaptions={projectLabelCaptions}
          listActive={listActive}
          hasLoadedAllProjects={hasLoadedAllProjects}
          hasValidCurrentWorkspace={hasValidCurrentWorkspace}
          currentAccountHasCurrentSubscription={currentAccountHasCurrentSubscription}
          handleOpenExportationPopup={handleOpenExportationPopup}
        />
      </div>
    )
  }

  return (
    <div className={classnames(
      'ProjectsTable',
      { 'ProjectsTable__full-languages-or-project-versions-view': showDashboardWithFullLanguagesOrProjectVersionsView }
    )}>
      {renderProjectsTableMain()}
      {hasProjectsSelected && (
        <ProjectsBulkActionBar />
      )}
    </div>
  )
}

export default ProjectsTable
